import { KeyboardEvent, ReactNode, useId, useState } from "react";
import { capitalize } from "lodash";
import { useQuery } from "@tanstack/react-query";
import { Button, Icon, IconButton, Input, Spinner, cn } from "@playerelite/ui";
import { searchVenuePromotions } from "../api/new-api";
import { PromotionSearchResult } from "../types";
import {
  filterPromotions,
  LinkableContentType,
  linkNoun,
} from "./attractor-content";

const MAX_RESULTS = 50;

interface Props {
  venueId: number | undefined;
  contentType: LinkableContentType;
  value: PromotionSearchResult | null;
  onChange: (value: PromotionSearchResult | null) => void;
  disabled?: boolean;
}

/**
 * Search-and-select for the game or promotion an attractor links to.
 *
 * The endpoint returns every eligible entry for the venue, so the list is fetched once per
 * content type, cached, and filtered as the user types. The input takes its label and ARIA
 * wiring from the surrounding kit FormField.
 *
 * The results list sits in the normal flow rather than floating: inside a Dialog's scroll box
 * a floating list is clipped, and the kit has no combobox to portal it for us.
 */
const PromotionSearchSelect = ({
  venueId,
  contentType,
  value,
  onChange,
  disabled,
}: Props) => {
  const noun = linkNoun(contentType);
  const listId = useId();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const promotions = useQuery({
    queryKey: ["venue-promotions", venueId, contentType],
    queryFn: async () => {
      const res = await searchVenuePromotions(venueId as number, contentType);
      return res?.data?.promotions ?? [];
    },
    enabled: venueId !== undefined,
    staleTime: 5 * 60 * 1000,
    // One retry, not the default three: the user is waiting on this list mid-form.
    retry: 1,
  });

  const { matches, total } = filterPromotions(
    promotions.data ?? [],
    query,
    MAX_RESULTS
  );
  const active = matches[Math.min(activeIndex, matches.length - 1)];
  const optionId = (p: PromotionSearchResult) => `${listId}-${p.promotionId}`;

  const select = (p: PromotionSearchResult) => {
    onChange(p);
    setQuery("");
    setOpen(false);
  };

  // Escape is left alone: the Dialog around this catches it first, in the capture phase.
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => Math.min(i + 1, matches.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && open && active) {
      e.preventDefault();
      select(active);
    }
  };

  let status: ReactNode = null;
  if (promotions.isPending) {
    status = (
      <span className="flex items-center gap-2">
        <Spinner size="xs" label={null} />
        Loading {noun}s…
      </span>
    );
  } else if (promotions.isError) {
    status = (
      <span className="flex items-center gap-2 text-danger-subtle-fg">
        Couldn't load {noun}s.
        <Button variant="link" size="sm" onClick={() => promotions.refetch()}>
          Try again
        </Button>
      </span>
    );
  } else if (total === 0) {
    status = query.trim()
      ? `No ${noun}s match "${query.trim()}".`
      : `No active ${noun}s at this venue.`;
  } else if (total > matches.length) {
    status = `Showing ${matches.length} of ${total}. Keep typing to narrow the list.`;
  }

  return (
    <div className="flex flex-col gap-2">
      {value ? (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-border bg-surface-sunken py-1 pl-3 pr-1">
          <span className="flex min-w-0 items-center gap-2 text-sm text-fg">
            <Icon
              name={contentType === "Game" ? "gamepad" : "tag"}
              size="sm"
              className="shrink-0 text-fg-muted"
            />
            <span className="truncate font-medium">{value.displayName}</span>
            <span className="shrink-0 font-mono text-xs text-fg-subtle">
              #{value.promotionId}
            </span>
          </span>
          <IconButton
            aria-label={`Remove linked ${noun}`}
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={() => onChange(null)}
          >
            <Icon name="x" size="sm" />
          </IconButton>
        </div>
      ) : null}

      <Input
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && active ? optionId(active) : undefined}
        autoComplete="off"
        placeholder={
          value
            ? `Search to change the ${noun}…`
            : `Search ${noun}s at this venue…`
        }
        startAdornment={<Icon name="search" size="sm" />}
        value={query}
        disabled={disabled || venueId === undefined}
        onChange={(e) => {
          setQuery(e.target.value);
          setActiveIndex(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={onKeyDown}
      />

      {open ? (
        // Holding focus in the input on mousedown keeps the list open long enough for the
        // click on an option (or on "Try again") to land.
        <div
          className="overflow-hidden rounded-lg border border-border bg-surface-raised shadow-sm"
          onMouseDown={(e) => e.preventDefault()}
        >
          <ul
            id={listId}
            role="listbox"
            aria-label={`${capitalize(noun)}s`}
            className="max-h-56 overflow-y-auto"
          >
            {matches.map((p, i) => (
              <li
                key={p.promotionId}
                id={optionId(p)}
                role="option"
                aria-selected={p.promotionId === value?.promotionId}
                className={cn(
                  "flex cursor-pointer items-center justify-between gap-3 px-3 py-2 text-sm text-fg",
                  p === active && "bg-action-subtle text-action-subtle-fg"
                )}
                onMouseMove={() => setActiveIndex(i)}
                onClick={() => select(p)}
              >
                <span className="flex min-w-0 items-center gap-2">
                  {p.promotionId === value?.promotionId ? (
                    <Icon name="check" size="xs" className="shrink-0" />
                  ) : null}
                  <span className="truncate">{p.displayName}</span>
                </span>
                <span className="shrink-0 font-mono text-xs text-fg-subtle">
                  #{p.promotionId}
                </span>
              </li>
            ))}
          </ul>
          {status ? (
            <p
              role="status"
              className={cn(
                "px-3 py-2 text-xs text-fg-muted",
                matches.length > 0 && "border-t border-border"
              )}
            >
              {status}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default PromotionSearchSelect;
